import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import Navbar from "./components/Navbar";
import GlobalPromoBanner from "./components/GlobalPromoBanner";
import UmamiAnalytics from "./components/UmamiAnalytics";
import AdblockAnalytics from "./components/AdblockAnalytics";
import DelayedAdsenseLoader from "./components/DelayedAdsenseLoader";
import WebMcpProvider from "./components/WebMcpProvider";
import HomePage from "./pages/HomePage";
import ErrorBoundary from "./components/ErrorBoundary";
import { ADSENSE_CLIENT_ID, ADSENSE_ENABLED } from "./config/env";
import { ONGKIR_COURIERS } from "./constants/ongkirCouriers";
import { RESI_COURIERS } from "./constants/resiCouriers";
// Lazy loaded pages — each page is only fetched when the user navigates to it
const CekResiPage = lazy(() => import("./pages/CekResiPage"));
const OngkirPage = lazy(() => import("./pages/OngkirPage"));
const TrackingResultPage = lazy(() => import("./pages/TrackingResultPage"));
const CourierListPage = lazy(() => import("./pages/CourierListPage"));
const CourierPage = lazy(() => import("./pages/CourierPage"));
const OngkirDetailPage = lazy(() => import("./pages/OngkirDetailPage"));
const BlogListPage = lazy(() => import("./pages/BlogListPage"));
const CourierAssetDownloadPage = lazy(() => import("./pages/CourierAssetDownloadPage"));
const CourierAssetDetailPage = lazy(() => import("./pages/CourierAssetDetailPage"));
const AboutContactPage = lazy(() => import("./pages/AboutContactPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const VerifiedSeedbacklinkPage = lazy(() => import("./pages/VerifiedSeedbacklinkPage"));
const WebMcpPage = lazy(() => import("./pages/WebMcpPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const OngkirResultPage = lazy(() => import("./pages/OngkirResultPage"));
const OngkirHistoryPage = lazy(() => import("./pages/OngkirHistoryPage"));
const OngkirPlaygroundPage = lazy(() => import("./pages/OngkirPlayground"));
const KelurahanPlaygroundPage = lazy(() => import("./pages/KelurahanPlayground"));
const PostalCodePage = lazy(() => import("./pages/PostalCodePage"));
const PostalProvincePage = lazy(() => import("./pages/PostalProvincePage"));
const PostalCityPage = lazy(() => import("./pages/PostalCityPage"));
const KodePosSaatIniPage = lazy(() => import("./pages/KodePosSaatIniPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

export default function App() {
  const location = useLocation();
  const [isNavigating, setIsNavigating] = useState(false);
  // Compare against the last seen location instead of a "has mounted" flag: StrictMode
  // re-runs mount effects in development, and a flag would then fire a fake navigation
  // (state update + scroll) on first load, which also breaks hydration of prerendered pages.
  const lastLocationRef = useRef(`${location.pathname}${location.search}`);
  const indicatorTimerRef = useRef(null);

  useEffect(() => {
    const currentLocation = `${location.pathname}${location.search}`;
    if (lastLocationRef.current === currentLocation) {
      return;
    }
    lastLocationRef.current = currentLocation;

    setIsNavigating(true);
    if (indicatorTimerRef.current) {
      clearTimeout(indicatorTimerRef.current);
    }

    window.scrollTo({ top: 0, behavior: "auto" });

    indicatorTimerRef.current = window.setTimeout(() => {
      setIsNavigating(false);
    }, 450);

    return () => {
      if (indicatorTimerRef.current) {
        clearTimeout(indicatorTimerRef.current);
      }
    };
  }, [location.pathname, location.search]);

  // MutationObserver to prevent third-party scripts (like Google AdSense) from injecting
  // layout-collapsing inline styles ("height: auto !important; min-height: 0px !important;")
  // that override our min-height constraints and cause Cumulative Layout Shift (CLS).
  useEffect(() => {
    if (typeof window === "undefined" || typeof MutationObserver === "undefined") {
      return;
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "attributes" && mutation.attributeName === "style") {
          const target = mutation.target;
          if (!target) return;

          const isPageEl = target.classList && target.classList.contains("page");
          const isMainEl =
            target.tagName === "MAIN" ||
            (target.classList && target.classList.contains("main-content"));
          const isBodyOrHtml = target.tagName === "BODY" || target.tagName === "HTML";

          if (isPageEl || isMainEl || isBodyOrHtml) {
            const heightAttr = target.style.height;
            const minHeightAttr = target.style.minHeight;

            if (heightAttr && heightAttr.includes("auto")) {
              target.style.removeProperty("height");
            }
            if (
              minHeightAttr &&
              (minHeightAttr.includes("0px") || minHeightAttr === "0px" || minHeightAttr === "0")
            ) {
              target.style.removeProperty("min-height");
            }
          }
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      subtree: true,
      attributeFilter: ["style"]
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <WebMcpProvider>
    <div className="app-shell">
      <UmamiAnalytics />
      <AdblockAnalytics />
      <Helmet prioritizeSeoTags>
        {ADSENSE_ENABLED && ADSENSE_CLIENT_ID ? (
          <>
            <meta name="google-adsense-account" content={ADSENSE_CLIENT_ID} />
            <link rel="dns-prefetch" href="//googleads.g.doubleclick.net" />
          </>
        ) : null}
      </Helmet>
      <DelayedAdsenseLoader />
      <div
        className={`page-transition-indicator ${isNavigating ? "active" : ""}`}
        aria-hidden="true"
      />
      <Navbar />
      <div className="page">
        <ErrorBoundary>
          <Suspense
            fallback={
              <div className="page-loading" role="status" aria-live="polite">
                Memuat halaman...
              </div>
            }
          >
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/deteksi-otomatis" element={<CekResiPage />} />
              <Route path="/deteksi-otomatis/:resi" element={<CekResiPage />} />
              <Route path="/cek-resi" element={<Navigate to="/deteksi-otomatis" replace />} />
              <Route path="/cek-resi/:resi" element={<Navigate to="/deteksi-otomatis" replace />} />
              <Route path="/kurir" element={<CourierListPage />} />
              <Route path="/download-logo-kurir" element={<CourierAssetDownloadPage />} />
              <Route path="/download-logo-kurir/:courier" element={<CourierAssetDetailPage />} />
              <Route path="/kurir/:courier" element={<CourierPage />} />
              <Route path="/kurir/:courier/:resi" element={<TrackingResultPage />} />
              <Route path="/ongkir" element={<OngkirPage />} />
              <Route path="/ongkir/history" element={<OngkirHistoryPage />} />
              <Route path="/ongkir/playground" element={<OngkirPlaygroundPage />} />
              <Route path="/kodepos-kelurahan" element={<KelurahanPlaygroundPage />} />
              <Route path="/kode-pos-saya" element={<KodePosSaatIniPage />} />
              <Route path="/kodepos-provinsi" element={<PostalProvincePage />} />
              <Route path="/kodepos-provinsi/:provinceSlug" element={<PostalProvincePage />} />
              <Route path="/kodepos-provinsi/:provinceSlug/:citySlug" element={<PostalCityPage />} />
              <Route path="/kodepos/:postalCode" element={<PostalCodePage />} />
              <Route path="/tentang-kontak" element={<AboutContactPage />} />
              <Route path="/verified-seedbacklink" element={<VerifiedSeedbacklinkPage />} />
              <Route path="/webmcp" element={<WebMcpPage />} />
              <Route path="/privacy-policy" element={<LegalPage type="privacy" />} />
              <Route path="/terms-of-service" element={<LegalPage type="terms" />} />
              <Route path="/blog" element={<BlogListPage />} />
              <Route path="/blog/page/:pageNumber" element={<BlogListPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/ongkir/:originId/:destinationId/:weight/:couriers" element={<OngkirResultPage />} />
              <Route path="/ongkir/:courier" element={<OngkirDetailPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </div>
      <GlobalPromoBanner />
      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-links">
            <div className="footer-section">
              <div className="footer-section-title">Cek Ongkir</div>
              <nav className="footer-courier-links">
                {ONGKIR_COURIERS.map((courier) => (
                  <Link
                    key={courier.code}
                    to={`/ongkir/${courier.code}`}
                    className="footer-link"
                  >
                    {courier.name}
                  </Link>
                ))}
              </nav>
            </div>
            <div className="footer-section">
              <div className="footer-section-title">Lacak Resi</div>
              <nav className="footer-courier-links">
                {RESI_COURIERS.map((courier) => (
                  <Link
                    key={courier.value}
                    to={`/kurir/${courier.value}`}
                    className="footer-link"
                  >
                    {courier.label}
                  </Link>
                ))}
              </nav>
            </div>
            <div className="footer-section">
              <div className="footer-section-title">Informasi</div>
              <nav className="footer-courier-links footer-info-links">
                <Link to="/kodepos-kelurahan" className="footer-link">
                  Kode Pos
                </Link>
                <Link to="/kode-pos-saya" className="footer-link">
                  Kode Pos Saya
                </Link>
                <Link to="/ongkir/history" className="footer-link">
                  Riwayat Cek Ongkir
                </Link>
                <Link to="/download-logo-kurir" className="footer-link">
                  Download Logo Kurir
                </Link>
                <Link to="/tentang-kontak" className="footer-link">
                  Tentang &amp; Kontak
                </Link>
                <Link to="/webmcp" className="footer-link">
                  WebMCP Tools
                </Link>
                <Link to="/verified-seedbacklink" className="footer-link">
                  Verified Seedbacklink
                </Link>
                <Link to="/privacy-policy" className="footer-link">
                  Kebijakan Privasi
                </Link>
                <Link to="/terms-of-service" className="footer-link">
                  Syarat dan Ketentuan
                </Link>
              </nav>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </WebMcpProvider>
  );
}
